import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Cartao,
  TituloDeSecao,
} from '../componentes/base';
import { Campo, Caixa, Seletor } from '../componentes/campos';
import { moeda } from '../util/formato';

interface LinhaDaMemoria {
  readonly rubrica: string;
  readonly base: string;
  readonly valor: string;
  readonly destaque: 'nenhum' | 'subtotal' | 'total';
}

interface Resultado {
  readonly principal: number;
  readonly periodoPorExtenso: string;
  readonly total: number;
  readonly totalFormatado: string;
  readonly multiplicador: number;
  readonly memoria: LinhaDaMemoria[];
}

export function PaginaCalculadora() {
  const [principal, setPrincipal] = useState('');
  const [dataVencimento, setDataVencimento] = useState('');
  const [dataCalculo, setDataCalculo] = useState(new Date().toISOString().slice(0, 10));
  const [indiceCorrecao, setIndiceCorrecao] = useState('');
  const [percentualMulta, setPercentualMulta] = useState('');
  const [percentualJuros, setPercentualJuros] = useState('');
  const [tipoJuros, setTipoJuros] = useState('SIMPLES');
  const [baseJuros, setBaseJuros] = useState('CORRIGIDO');
  const [percentualHonorarios, setPercentualHonorarios] = useState('');
  const [custas, setCustas] = useState('');
  const [proRata, setProRata] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const calcular = useMutation({
    mutationFn: () =>
      api.criar<Resultado>('/calculos/calcular', {
        principal,
        dataVencimento: dataVencimento || undefined,
        dataCalculo,
        indiceCorrecao: indiceCorrecao || 0,
        percentualMulta: percentualMulta || 0,
        percentualJuros: percentualJuros || 0,
        tipoJuros,
        baseJuros,
        percentualHonorarios: percentualHonorarios || 0,
        custas: custas || 0,
        proRata,
      }),
    /* Sem isto, um cálculo recusado deixava a área do resultado exatamente como
       estava — inclusive com o resultado ANTERIOR na tela, que é pior do que
       vazio: parece que recalculou e deu o mesmo número. */
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível calcular.'),
    onSuccess: () => setErro(null),
  });

  const r = calcular.data;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Calculadora de débito"
        descricao="Atualização do valor devido, com memória de cálculo para instruir a petição"
      />

      {erro && (
        <div className="mb-5">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      <div className="mb-5">
        <Alerta>
          <strong>Nenhum índice é presumido.</strong> Correção, multa, juros e honorários
          são informados por você, conforme o título que embasa a obrigação — um sistema
          que escolhe o índice sozinho produz demonstrativo que não se sustenta em juízo.
        </Alerta>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1.1fr]">
        <Cartao>
          <TituloDeSecao>Parâmetros</TituloDeSecao>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Valor principal"
              valor={principal}
              aoMudar={setPrincipal}
              placeholder="1.850,00"
            />
            <Campo
              rotulo="Vencimento da obrigação"
              tipo="date"
              valor={dataVencimento}
              aoMudar={setDataVencimento}
            />
            <Campo
              rotulo="Data-base do cálculo"
              tipo="date"
              valor={dataCalculo}
              aoMudar={setDataCalculo}
            />
            <Campo
              rotulo="Correção monetária acumulada (%)"
              valor={indiceCorrecao}
              aoMudar={setIndiceCorrecao}
              placeholder="4,5"
              auxilio="do período inteiro, não ao mês"
            />
            <Campo
              rotulo="Multa (%)"
              valor={percentualMulta}
              aoMudar={setPercentualMulta}
              placeholder="2"
            />
            <Campo
              rotulo="Juros de mora ao mês (%)"
              valor={percentualJuros}
              aoMudar={setPercentualJuros}
              placeholder="1"
            />
            <Seletor
              rotulo="Tipo de juros"
              valor={tipoJuros}
              aoMudar={setTipoJuros}
              opcoes={[
                { valor: 'SIMPLES', rotulo: 'Simples' },
                { valor: 'COMPOSTOS', rotulo: 'Compostos' },
              ]}
            />
            <Seletor
              rotulo="Juros incidem sobre"
              valor={baseJuros}
              aoMudar={setBaseJuros}
              opcoes={[
                { valor: 'PRINCIPAL', rotulo: 'O principal' },
                { valor: 'CORRIGIDO', rotulo: 'O principal corrigido' },
                { valor: 'CORRIGIDO_COM_MULTA', rotulo: 'O corrigido mais a multa' },
              ]}
            />
            <Campo
              rotulo="Honorários advocatícios (%)"
              valor={percentualHonorarios}
              aoMudar={setPercentualHonorarios}
              placeholder="20"
              auxilio="sobre o subtotal"
            />
            <Campo
              rotulo="Custas processuais"
              valor={custas}
              aoMudar={setCustas}
              placeholder="350,00"
            />
          </div>

          <div className="mt-4">
            <Caixa
              rotulo="Contar a fração de mês proporcionalmente (pro rata)"
              marcado={proRata}
              aoMudar={setProRata}
            />
          </div>

          <div className="mt-5">
            <Botao
              tipo="primario"
              onClick={() => calcular.mutate()}
              desabilitado={calcular.isPending || !principal}
            >
              {calcular.isPending ? 'Calculando…' : 'Calcular'}
            </Botao>
          </div>
        </Cartao>

        <Cartao semPadding>
          <div className="p-5 pb-3">
            <TituloDeSecao auxiliar={r ? r.periodoPorExtenso : undefined}>
              Memória de cálculo
            </TituloDeSecao>
          </div>

          {!r ? (
            <p className="px-5 pb-6 text-sm text-[color:var(--texto-tenue)]">
              Informe ao menos o valor principal e acione Calcular. A memória aparece aqui,
              linha a linha, na ordem de incidência da praxe forense.
            </p>
          ) : (
            <>
              <table className="w-full border-t text-sm">
                <tbody>
                  {r.memoria.map((linha, i) => (
                    <tr
                      key={`${linha.rubrica}-${i}`}
                      className="border-b last:border-0"
                      style={
                        linha.destaque !== 'nenhum'
                          ? { background: 'var(--superficie-afundada)' }
                          : undefined
                      }
                    >
                      <td
                        className={`px-5 py-2.5 ${
                          linha.destaque === 'total' ? 'font-semibold' : ''
                        }`}
                        style={
                          linha.destaque === 'total'
                            ? { color: 'var(--color-recebido)' }
                            : undefined
                        }
                      >
                        {linha.rubrica}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-[color:var(--texto-tenue)]">
                        {linha.base}
                      </td>
                      <td
                        className={`tabular px-5 py-2.5 text-right whitespace-nowrap ${
                          linha.destaque === 'total' ? 'font-semibold' : ''
                        }`}
                        style={
                          linha.destaque === 'total'
                            ? { color: 'var(--color-recebido)' }
                            : undefined
                        }
                      >
                        {linha.valor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="p-5">
                <p className="text-xs text-[color:var(--texto-suave)]">
                  O total equivale a{' '}
                  <strong className="tabular">
                    {String(r.multiplicador).replace('.', ',')} vez(es)
                  </strong>{' '}
                  o valor principal de {moeda(r.principal)}.
                </p>
              </div>
            </>
          )}
        </Cartao>
      </div>
    </>
  );
}
